import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { MaintenanceDate } from '../../../model/maintenance-schedule.model';
import { selectMaintenanceScheduleDate } from '../../../store/maintenance-schedule/maintenance-schedule.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { maintenanceScheduleActions } from '../../../store/maintenance-schedule/maintenance-schedule.actions';
import { DEFAULT_DATE_STRING_CONFIG_NO_SECONDS } from '../../../model/data-table.model';
import { DialogHelperService } from '../../../service/helper/dialog.helper.service';

@Component({
    selector: 'app-maintenance-management',
    templateUrl: './maintenance-management.component.html',
    styleUrl: './maintenance-management.component.scss',
})
export class MaintenanceManagementComponent implements OnInit {
    private store = inject(Store);
    private destroyRef = inject(DestroyRef);
    private dialogHelperService = inject(DialogHelperService);

    newMaintenanceDate: MaintenanceDate;

    maintenanceDate: MaintenanceDate | null;
    dateChanged = false;

    ngOnInit(): void {
        this.store.select(selectMaintenanceScheduleDate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((maintenanceDate) => {
            if (
                !(this.maintenanceDate?.maintenanceStartDate.valueOf() === maintenanceDate?.maintenanceStartDate.valueOf()
                    && this.maintenanceDate?.maintenanceEndDate.valueOf() === maintenanceDate?.maintenanceEndDate.valueOf())
            ) {
                this.maintenanceDate = maintenanceDate;
                this.dateChanged = false;
            }
        });
        this.newMaintenanceDate = this.maintenanceDate ? this.maintenanceDate : { maintenanceStartDate: new Date(), maintenanceEndDate: new Date() };
    }

    /**
     * set the date  from the date time input group to the correct maintenanceDate
     * @param date the selected date
     * @param dateType if its the start or end time for the maintenance
     */
    handleDateSelection(date: Date, dateType: 'start' | 'end'): void {
        this.dateChanged = true;
        if (dateType === 'start') {
            this.newMaintenanceDate = { ...this.newMaintenanceDate, maintenanceStartDate: date };
        } else {
            this.newMaintenanceDate = { ...this.newMaintenanceDate, maintenanceEndDate: date };
        }
    }

    /**
     * trigger the setting of the maintenance window for the user
     */
    handleSetMaintenance(): void {
        this.handleMaintenanceChanges(maintenanceScheduleActions.updateMaintenanceSchedule({ maintenanceDate: this.newMaintenanceDate }))
    }

    /**
     * trigger the stop of the maintenance window for the user
     */
    handleStopMaintenance(): void {
        this.handleMaintenanceChanges(maintenanceScheduleActions.updateStopMaintenanceSchedule());
    }

    /**
     * Function to open a confirmation dialog before the maintenance is changed
     * @param action the action that should be triggered if the user confirms the changes
     */
    handleMaintenanceChanges(action: Action): void {
        this.dialogHelperService.openNewDialog({
            title: 'Sind Sie sich sicher, das sie den Wartungszeitraum ändern wollen?',
            actions: [
                {
                    title: 'Abbrechen',
                    clickFunction: (): void => {
                        this.dialogHelperService.closeLastDialog();
                    },
                },
                {
                    title: 'Wartungszeitraum ändern',
                    clickFunction: (): void => {
                        this.store.dispatch(action);
                        this.dialogHelperService.closeLastDialog();

                    }
                }
            ]
        });
    }

    /**
     * get the active date to display below the selection inputs
     * @param isStart boolean if it should return the start date
     * @returns return the value as local string
     */
    activeDate(isStart: boolean): string {
        if (this.maintenanceDate) {
            return isStart ?
                this.maintenanceDate.maintenanceStartDate.toLocaleString("de-DE", DEFAULT_DATE_STRING_CONFIG_NO_SECONDS) :
                this.maintenanceDate.maintenanceEndDate.toLocaleString("de-DE", DEFAULT_DATE_STRING_CONFIG_NO_SECONDS);
        }
        return "";
    }

    /**
     * check if the maintenance times are different and can be updated
     * @returns boolean if the the button should be disabled or not
     */
    canUpdateMaintenanceWindow(): boolean {
        if (this.newMaintenanceDate) {
            const endDateNotInPast = this.newMaintenanceDate.maintenanceEndDate.valueOf() > Date.now();
            const endAfterStart = this.newMaintenanceDate.maintenanceEndDate.valueOf() > this.newMaintenanceDate.maintenanceStartDate.valueOf();
            return this.dateChanged && endAfterStart && endDateNotInPast;
        }
        return false;

    }
    /**
     * check if the maintenance date and stop maintenance button should be displayed
     * @returns boolean if it should be displayed or not
     */
    maintenanceDateSet(): boolean {
        const notEmpty = !!this.maintenanceDate?.maintenanceStartDate && !!this.maintenanceDate?.maintenanceEndDate
        return !!this.maintenanceDate && notEmpty;
    }
}
